import {
	ButtonWithLoading,
	ErrorModal,
	MesfModal,
	getMomentTz,
	useUTLSettingsContext,
} from "@dexteel/mesf-core";
import { Button, Grid, Snackbar, TextField } from "@material-ui/core";
import { Autocomplete } from "@material-ui/lab";
import MuiAlert from "@material-ui/lab/Alert";
import { get } from "lodash-es";
import React, { useEffect, useState } from "react";
import { Controller, SubmitHandler, useForm } from "react-hook-form";
import { CatalogMaterial } from "../../../../../steel/models/CatalogMaterial";
import { useModalStyles } from "../../../../eaf-processes/styles/modalStyles";
import { useDataEntryContext } from "../../../context/reducer.provider";
import { useEAFDataEntryActions } from "../../../hooks/useEAFDataEntryActions";
import { WireAddition } from "../../../models/eaf.models";
import { adjustTimestampDate } from "../../../utils/adjustTimestampDate";
import { DateTimePickerDE } from "../../form-fields/common/DateTimePickerDE";
import { NumberTextFieldDE } from "../../form-fields/common/NumberTextFieldDE";
import { validateField } from "../../tables/utils/validateMinMax";

type Props = {
	show: boolean;
	onHide: () => void;
	date: Date;
};
const moment = getMomentTz();
export const CreateWireAddition = ({ show, onHide, date }: Props) => {
	const { state } = useUTLSettingsContext();
	const MIN_FT_WIRE_ADDITION = get(
		state.settings,
		"EAF_DE_MIN_FT_WIRE_ADDITION",
		0,
	);
	const MAX_FT_WIRE_ADDITION = get(
		state.settings,
		"EAF_DE_MAX_FT_WIRE_ADDITION",
		2000,
	);

	const {
		state: { deProcess },
	} = useDataEntryContext();
	const { saveWireAddition, getMaterials } = useEAFDataEntryActions();
	const [isSubmitLoading, setIsSubmitLoading] = useState(false);
	const [error, setError] = useState("");
	const [open, setOpen] = useState(false);
	const [materials, setMaterials] = useState<CatalogMaterial[]>([]);
	const classes = useModalStyles();
	const {
		getValues,
		handleSubmit,
		setValue,
		control,
		reset,
		formState: { errors },
	} = useForm<WireAddition>({});

	const handleClose = (event?: React.SyntheticEvent, reason?: string) => {
		if (reason === "clickaway") return;
		setOpen(false);
	};
	const handleChangeTime = (val: Date | null) => {
		if (!val) return;

		const timestamp = moment.utc(val);
		const start = moment.utc(deProcess?.process.Start);
		const end = deProcess?.process.End
			? moment.utc(deProcess.process.End)
			: null;

		const adjustedTimestamp = adjustTimestampDate(timestamp, start, end);

		setValue("Start", adjustedTimestamp.toDate());
		setValue("End", adjustedTimestamp.toDate());
	};

	const onSubmit: SubmitHandler<any> = async (data: any) => {
		setIsSubmitLoading(true);
		await saveWireAddition(data);
		setIsSubmitLoading(false);
		onHide();
	};

	useEffect(() => {
		if (show) {
			setIsSubmitLoading(false);
			reset();
			(async () => {
				const materials: CatalogMaterial[] = await getMaterials();
				setMaterials(
					materials.filter((material) => material.UseInTrim === true),
				);
			})();
			setValue("Line", 1);
			setValue("MaterialId", null);
			setValue("Start", date);
			setValue("End", date);
			setValue("InjectedValue", 1);
		}
	}, [show]);

	return (
		<>
			<Grid container>
				<Grid item>
					<MesfModal
						title="CREATE WIRE ADDITION"
						open={show}
						handleClose={() => onHide()}
						id="create-wire-addition-modal"
						maxWidth="sm"
					>
						<form onSubmit={handleSubmit(onSubmit)}>
							<MesfModal.Content dividers style={{ padding: "15px 30px" }}>
								<Grid container spacing={2}>
									<Grid item xs={12} md={6}>
										<Controller
											name="Line"
											control={control}
											rules={{
												required: "Line is required",
												validate: (val) =>
													[1, 2, 3].includes(val) || "Line must be 1, 2 or 3",
											}}
											render={({ field }) => (
												<NumberTextFieldDE
													{...field}
													value={getValues("Line")}
													label="Line"
													onChange={(val) => setValue("Line", val)}
													onBlur={() => {}}
												/>
											)}
										/>
										{errors.Line && (
											<span className={classes.errorLabel}>
												{errors.Line.message}
											</span>
										)}
									</Grid>
									<Grid item xs={12} md={6}>
										<Controller
											name="MaterialId"
											control={control}
											rules={{ required: "Material is required" }}
											render={({ field }) => (
												<Autocomplete
													id="clear-on-escape"
													clearOnEscape
													options={materials ?? []}
													getOptionLabel={(option) => option.MaterialName}
													renderInput={(params) => (
														<TextField
															{...params}
															label="Material"
															variant="outlined"
															fullWidth
															size="small"
															InputProps={{
																...params.InputProps,
																className: classes.autocompleteField,
															}}
														/>
													)}
													value={
														materials.find(
															(material) => material.MaterialId === field.value,
														) || null
													}
													onChange={(event, newValue) => {
														field.onChange(
															newValue ? newValue.MaterialId : null,
														);
													}}
												/>
											)}
										/>
										{errors.MaterialId && (
											<span className={classes.errorLabel}>
												{errors.MaterialId.message}
											</span>
										)}
									</Grid>
									<Grid item xs={12} md={6} alignContent="center">
										<Controller
											name="End"
											control={control}
											rules={{ required: "Time is required" }}
											render={({ field }) => (
												<DateTimePickerDE
													{...field}
													format={"HH:mm - MM/DD/YYYY"}
													value={getValues("End")}
													label="Time"
													onChange={handleChangeTime}
													onBlur={() => {}}
												/>
											)}
										/>
										{errors.End && (
											<span className={classes.errorLabel}>
												{errors.End.message}
											</span>
										)}
									</Grid>
									<Grid item xs={12} md={6}>
										<Controller
											name="InjectedValue"
											control={control}
											rules={{
												required: "Injected value is required",
												validate: (val) =>
													validateField(
														val,
														MIN_FT_WIRE_ADDITION,
														MAX_FT_WIRE_ADDITION,
														"Injected",
													),
											}}
											render={({ field }) => (
												<NumberTextFieldDE
													{...field}
													value={getValues("InjectedValue")}
													label="Injected"
													onChange={(val) => setValue("InjectedValue", val)}
													onBlur={() => {}}
												/>
											)}
										/>
										{errors.InjectedValue && (
											<span className={classes.errorLabel}>
												{errors.InjectedValue.message}
											</span>
										)}
									</Grid>
								</Grid>
							</MesfModal.Content>
							<MesfModal.Actions style={{ padding: "20px" }}>
								<Grid container spacing={2} justifyContent="flex-end">
									<Grid item md={3} xs={12}>
										<Button
											fullWidth
											variant="contained"
											color="default"
											onClick={() => onHide()}
											size="large"
										>
											Cancel
										</Button>
									</Grid>
									<Grid item md={3} xs={12}>
										<ButtonWithLoading
											fullWidth
											isLoading={isSubmitLoading}
											variant="contained"
											color="primary"
											type="submit"
											size="large"
										>
											Save
										</ButtonWithLoading>
									</Grid>
								</Grid>
							</MesfModal.Actions>
						</form>
					</MesfModal>
					<Snackbar open={open} autoHideDuration={2500} onClose={handleClose}>
						<MuiAlert
							elevation={6}
							variant="filled"
							severity="success"
							onClose={handleClose}
						>
							The wire addition was created successfully
						</MuiAlert>
					</Snackbar>
				</Grid>
			</Grid>
			<ErrorModal
				error={error}
				onHide={() => setError("")}
				title="Error Creating Wire Addition"
			/>
		</>
	);
};
